import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import {
    Users,
    Search,
    Plus,
    Edit3,
    Trash2,
    Car,
    Mail,
    Phone,
    Smartphone,
    PhoneCall,
    User as UserIcon,
    Filter,
    Settings,
    Eye,
    MoreVertical,
    Warehouse,
    UsersRoundIcon,
} from 'lucide-react';

import { getCustomers, getCustomersOverview } from 'actions/customerActions';
import Pagination from 'components/common/Pagination';
import history from 'helpers/history';
import placeholders from 'helpers/placeholders';
import TableColumn from 'models/TableColumnModel';
import CustomerModal from 'components/modals/CustomerModal';
import Customer from 'models/CustomerModel';
import axios from 'helpers/axios';
import config from 'helpers/config';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import ColumnsPicker from 'components/common/ColumnsPicker';
import User from 'models/UserModel';
import actionTypes from 'helpers/actionTypes';
import PhoneNumberType from 'models/types/PhoneNumberTypes';
import getFullVehicleName from 'helpers/vehicleFullNameHelper';
import { FaUsers } from 'react-icons/fa';
import SearchResults from 'models/SearchResultsModel';

const { apiUrl } = config;

interface Props {
    currentUser: User;
    dispatch: Dispatch;
}

interface ComponentState {
    errorMessage: string | null;
    searchPhrase: string;
    page: number;
    itemsPerPage: number;
    customersCount: number;
    tableColumns: TableColumn[];
    isLoading: boolean;
    customers: Customer[];
    [x: string]: any;
}

class CustomersListPage extends React.Component<Props, ComponentState> {
    constructor(props: Props) {
        super(props);
        this.handleSearch = this.handleSearch.bind(this);
        this.getCustomers = this.getCustomers.bind(this);
        this.pageChanged = this.pageChanged.bind(this);
        this.modalClosed = this.modalClosed.bind(this);

        this.state = {
            customers: [],
            isLoading: true,
            errorMessage: null,
            searchPhrase: '',
            page: 1,
            itemsPerPage: 20,
            customersCount: 0,
            isCreateCustomerModalOpen: false,
            isEditCustomerModalOpen: false,
            tableColumns: customerListColumns,
        };
    }

    componentDidMount() {
        const { dispatch } = this.props;
        this.getCustomers();
    }

    getCustomers() {
        const { page, searchPhrase } = this.state;
        let { itemsPerPage } = this.state;
        // if (this.state.searchPhrase?.length > 0) {
        //     itemsPerPage = this.state.customersCount;
        // }

        axios
            .get<SearchResults<Customer>>(`${config.apiUrl}/customer`, { params: { page, itemsPerPage, searchPhrase } })
            .then((res) => {
                this.setState({ customers: res.data.rows, customersCount: res.data.count, isLoading: false });
            })
            .catch((_) => {
                console.log(_);
            });
    }

    handleSearch(event: React.FormEvent<HTMLInputElement>) {
        const { customersCount } = this.state;
        const searchPhrase = event.currentTarget.value;
        if (searchPhrase?.length > 1) {
            this.setState(
                {
                    searchPhrase,
                    page: 1,
                    itemsPerPage: 50,
                },
                this.getCustomers,
            );
        } else {
            this.setState(
                {
                    searchPhrase: '',
                    page: 1,
                    itemsPerPage: 20,
                },
                () => this.componentDidMount(),
            );
        }
    }

    openCustomer(customerId: string) {
        history.push(`/customer/${customerId}`);
    }

    editCustomer(customerId: string) {
        this.setState({ isEditCustomerModalOpen: true, editableCustomerId: customerId });
    }

    pageChanged(page?: number) {
        if (page) {
            this.setState({ page }, () => this.getCustomers());
        }
    }

    deleteCustomer(customer: Customer) {
        displayConfirmationModal(`${customer.firstName} ${customer.lastName}`)
            .then(() => {
                axios
                    .delete(`${apiUrl}/customer/${customer.id}`)
                    .then(() => {
                        this.componentDidMount();
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            })
            .catch((_) => _);
    }

    isColumnActive(columnName: string) {
        return !!this.props.currentUser?.userSetting?.customerListColumns
            ? (JSON.parse(this.props.currentUser?.userSetting?.customerListColumns) as TableColumn[]).find(
                  (column) => column.name === columnName,
              )?.active
            : this.state.tableColumns.find((column) => column.name === columnName)?.active;
    }

    modalClosed(customerCreated?: boolean, customerEdited?: boolean) {
        this.setState({ isCreateCustomerModalOpen: false, isEditCustomerModalOpen: false, editableCustomerId: null });
        if (customerCreated || customerEdited) {
            this.componentDidMount();
        }
    }

    getCustomerModal() {
        const { isCreateCustomerModalOpen, isEditCustomerModalOpen, editableCustomerId } = this.state;
        if (isEditCustomerModalOpen && !!editableCustomerId) {
            return (
                <CustomerModal
                    customerId={editableCustomerId}
                    onClose={(customerCreated?: boolean, customerEdited?: boolean) =>
                        this.modalClosed(customerCreated, customerEdited)
                    }
                ></CustomerModal>
            );
        } else if (isCreateCustomerModalOpen) {
            return (
                <CustomerModal
                    onClose={(customerCreated?: boolean, customerEdited?: boolean) =>
                        this.modalClosed(customerCreated, customerEdited)
                    }
                ></CustomerModal>
            );
        }
    }

    updateVisibleColumns(updatedColumns: TableColumn[]) {
        const { userSetting } = this.props.currentUser;
        userSetting.customerListColumns = JSON.stringify(updatedColumns);
        const { dispatch } = this.props;
        dispatch({ type: actionTypes.UPDATE_USER_SETTINGS_SUCCESS, payload: userSetting });
        axios.put(`${config.apiUrl}/user/settings`, userSetting);
        this.setState({ tableColumns: updatedColumns });
    }
    getPhoneNumberTypeIcon(phoneNumberType: PhoneNumberType) {
        if (phoneNumberType === PhoneNumberType.Mobile) {
            return <Smartphone className="inline text-lg" />;
        } else {
            return <PhoneCall className="inline text-lg" />;
        }
    }
    render() {
        const {
            customersCount,
            customerspage,
            itemsPerPage,
            isCreateCustomerModalOpen,
            isEditCustomerModalOpen,
            tableColumns,
            isLoading,
            customers,
            page,
            searchPhrase,
        } = this.state;
        const { currentUser } = this.props;

        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            {/* <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Customers</span>
                            </nav> */}

                            <h2 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                                <FaUsers className="h-8 w-8 text-blue-600" />
                                <span>Customer List</span>
                            </h2>
                            <p className="text-gray-600 mt-1">Manage your customer database and contact information.</p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => this.setState({ isCreateCustomerModalOpen: !isCreateCustomerModalOpen })}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                            >
                                <Plus className="h-4 w-4" />
                                <span>Add Customer</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    {/* Search and Statistics */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div className="flex-1 max-w-md">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                    <input
                                        onChange={this.handleSearch}
                                        name="searchPhrase"
                                        placeholder={placeholders.CUSTOMER_SEARCH}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2 text-sm text-gray-600">
                                    <Users className="h-4 w-4" />
                                    <span className="font-semibold">{customersCount} customers found</span>
                                </div>

                                <div className="border-l border-gray-300 pl-4">
                                    <ColumnsPicker
                                        onChange={(updatedColumns) => this.updateVisibleColumns(updatedColumns)}
                                        columns={
                                            currentUser?.userSetting?.customerListColumns
                                                ? JSON.parse(currentUser.userSetting.customerListColumns)
                                                : tableColumns
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>{' '}
                    {/* Customers Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-blue-500 text-white border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                            Customer
                                        </th>
                                        {this.isColumnActive('email') && (
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                Email
                                            </th>
                                        )}
                                        {this.isColumnActive('phoneNumbers') && (
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                Phone Numbers
                                            </th>
                                        )}
                                        {this.isColumnActive('vehicles') && (
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                Vehicles
                                            </th>
                                        )}
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {
                                        customers && customers.length > 0
                                            ? customers.map((customer, index) => (
                                                  <tr
                                                      key={'customer' + index}
                                                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                                                      onClick={() => history.push(`/customer/${customer.id}`)}
                                                  >
                                                      {/* Customer Info */}
                                                      <td className="px-6 py-4">
                                                          <div className="flex items-center space-x-3">
                                                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                                  <UserIcon className="w-5 h-5 text-blue-600" />
                                                              </div>
                                                              <div className="min-w-0 flex-1">
                                                                  <div className="flex items-center space-x-2">
                                                                      <p className="text-sm font-semibold text-gray-900 truncate">
                                                                          {customer.firstName} {customer.lastName}
                                                                      </p>
                                                                  </div>
                                                                  {customer.labels && customer.labels.length > 0 && (
                                                                      <div className="flex flex-wrap gap-1 mt-1">
                                                                          {customer.labels
                                                                              .slice(0, 2)
                                                                              .map((label, labelIndex) => (
                                                                                  <span
                                                                                      key={labelIndex}
                                                                                      style={{
                                                                                          backgroundColor: label.color,
                                                                                      }}
                                                                                      className="px-2 py-0.5 rounded text-xs font-medium text-white"
                                                                                  >
                                                                                      {label.title}
                                                                                  </span>
                                                                              ))}
                                                                          {customer.labels.length > 2 && (
                                                                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                                                                                  +{customer.labels.length - 2}
                                                                              </span>
                                                                          )}
                                                                      </div>
                                                                  )}
                                                              </div>
                                                          </div>
                                                      </td>

                                                      {/* Email */}
                                                      {this.isColumnActive('email') && (
                                                          <td className="px-6 py-4">
                                                              {customer.primaryEmail ? (
                                                                  <div className="flex items-center space-x-2">
                                                                      <Mail className="h-4 w-4 text-gray-400" />
                                                                      <span className="text-sm text-gray-900 truncate max-w-xs">
                                                                          {customer.primaryEmail}
                                                                      </span>
                                                                  </div>
                                                              ) : (
                                                                  <span className="text-sm text-gray-400">
                                                                      No email
                                                                  </span>
                                                              )}
                                                          </td>
                                                      )}

                                                      {/* Phone Numbers */}
                                                      {this.isColumnActive('phoneNumbers') && (
                                                          <td className="px-6 py-4">
                                                              {customer.phoneNumbers &&
                                                              customer.phoneNumbers.length > 0 ? (
                                                                  <div className="space-y-1">
                                                                      {customer.phoneNumbers
                                                                          .slice(0, 2)
                                                                          .map((number, phoneIndex) => (
                                                                              <a
                                                                                  key={'phoneNumber' + phoneIndex}
                                                                                  href={`tel:${number.phoneNumber}`}
                                                                                  onClick={(e: React.MouseEvent) =>
                                                                                      e.stopPropagation()
                                                                                  }
                                                                                  className="flex items-center space-x-1 text-sm text-gray-700 hover:text-blue-600 transition-colors"
                                                                              >
                                                                                  {this.getPhoneNumberTypeIcon(
                                                                                      number.type,
                                                                                  )}
                                                                                  <span>{number.phoneNumber}</span>
                                                                              </a>
                                                                          ))}
                                                                      {customer.phoneNumbers.length > 2 && (
                                                                          <span className="text-xs text-gray-500">
                                                                              +{customer.phoneNumbers.length - 2} more
                                                                          </span>
                                                                      )}
                                                                  </div>
                                                              ) : (
                                                                  <span className="text-sm text-gray-400">
                                                                      No phone
                                                                  </span>
                                                              )}
                                                          </td>
                                                      )}

                                                      {/* Vehicles */}
                                                      {this.isColumnActive('vehicles') && (
                                                          <td className="px-6 py-4">
                                                              {customer.vehicles && customer.vehicles.length > 0 ? (
                                                                  <div className="space-y-1">
                                                                      <div className="flex items-center space-x-1 text-sm text-gray-600">
                                                                          <Car className="h-4 w-4" />
                                                                          <span className="font-medium">
                                                                              {customer.vehicles.length} vehicle
                                                                              {customer.vehicles.length !== 1
                                                                                  ? 's'
                                                                                  : ''}
                                                                          </span>
                                                                      </div>
                                                                      {customer.vehicles
                                                                          .slice(0, 2)
                                                                          .map((vehicle, vehicleIndex) => (
                                                                              <div
                                                                                  key={'vehicle' + vehicleIndex}
                                                                                  onClick={(e: React.MouseEvent) => {
                                                                                      e.stopPropagation();
                                                                                      history.push(
                                                                                          `/vehicle/${vehicle.id}`,
                                                                                      );
                                                                                  }}
                                                                                  className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer transition-colors"
                                                                              >
                                                                                  {getFullVehicleName(vehicle)}
                                                                              </div>
                                                                          ))}
                                                                      {customer.vehicles.length > 2 && (
                                                                          <span className="text-xs text-gray-500">
                                                                              +{customer.vehicles.length - 2} more
                                                                          </span>
                                                                      )}
                                                                  </div>
                                                              ) : (
                                                                  <span className="text-sm text-gray-400">
                                                                      No vehicles
                                                                  </span>
                                                              )}
                                                          </td>
                                                      )}

                                                      {/* Actions */}
                                                      <td className="px-6 py-4 text-right">
                                                          <div className="flex items-center justify-end space-x-2">
                                                              <button
                                                                  onClick={(e: React.MouseEvent) => {
                                                                      e.stopPropagation();
                                                                      history.push(`/customer/${customer.id}`);
                                                                  }}
                                                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                  title="View customer"
                                                              >
                                                                  <Eye className="h-4 w-4" />
                                                              </button>
                                                              <button
                                                                  onClick={(e: React.MouseEvent) => {
                                                                      e.stopPropagation();
                                                                      this.editCustomer(String(customer.id));
                                                                  }}
                                                                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                                  title="Edit customer"
                                                              >
                                                                  <Edit3 className="h-4 w-4" />
                                                              </button>
                                                              <button
                                                                  onClick={(e: React.MouseEvent) => {
                                                                      e.stopPropagation();
                                                                      this.deleteCustomer(customer);
                                                                  }}
                                                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                  title="Delete customer"
                                                              >
                                                                  <Trash2 className="h-4 w-4" />
                                                              </button>
                                                          </div>
                                                      </td>
                                                  </tr>
                                              ))
                                            : null
                                        // <tr>
                                        //     <td colSpan={5} className="px-6 py-12 text-center">
                                        //         <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                        //         {/* <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        //             {isLoading ? 'Loading customers...' : 'No customers found'}
                                        //         </h3> */}
                                        //         {!isLoading && (
                                        //             <p className="text-gray-500 mb-6">
                                        //                 {this.state.searchPhrase
                                        //                     ? 'Try adjusting your search criteria or add a new customer.'
                                        //                     : 'Get started by adding your first customer to the system.'}
                                        //             </p>
                                        //         )}
                                        //         {!isLoading && (
                                        //             <button
                                        //                 onClick={(e: React.MouseEvent) => {
                                        //                     e.stopPropagation();
                                        //                     this.setState({ isCreateCustomerModalOpen: true });
                                        //                 }}
                                        //                 className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 mx-auto transition-colors"
                                        //             >
                                        //                 <Plus className="h-4 w-4" />
                                        //                 <span>Add First Customer</span>
                                        //             </button>
                                        //         )}
                                        //     </td>
                                        // </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>{' '}
                    {/* Empty State - if no customers and not in table */}
                    {(!customers || customers.length === 0) && (
                        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {isLoading ? 'Loading customers...' : 'No customers found'}
                            </h3>
                            {!isLoading && (
                                <p className="text-gray-500 mb-6">
                                    {(customersCount > 0 && searchPhrase) || searchPhrase?.length > 0
                                        ? 'Try adjusting your search criteria or add a new customer.'
                                        : 'Get started by adding your first customer to the system.'}
                                </p>
                            )}
                            {!isLoading && customersCount < 1 && (searchPhrase === null || searchPhrase === '') && (
                                <button
                                    onClick={() => this.setState({ isCreateCustomerModalOpen: true })}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 mx-auto transition-colors"
                                >
                                    <Plus className="h-4 w-4" />
                                    <span>Add First Customer</span>
                                </button>
                            )}
                        </div>
                    )}
                    {/* Pagination */}
                    {customers && customers.length > 0 && (
                        <div className="mt-8 flex justify-center">
                            <Pagination
                                currentPage={page}
                                pageSize={itemsPerPage}
                                totalItems={customersCount}
                                pageChanged={this.pageChanged}
                            />
                        </div>
                    )}
                </div>

                {/* Modals */}
                {(isEditCustomerModalOpen || isCreateCustomerModalOpen) && this.getCustomerModal()}
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(CustomersListPage);

const customerListColumns: TableColumn[] = [
    {
        name: 'firstName',
        active: true,
        sortable: true,
        text: 'First Name',
    },
    {
        name: 'lastName',
        active: true,
        sortable: true,
        text: 'Last Name',
    },
    {
        name: 'email',
        active: true,
        sortable: true,
        text: 'Email',
    },
    {
        name: 'phoneNumbers',
        active: true,
        sortable: false,
        text: 'Phone numbers',
    },
    {
        name: 'vehicles',
        active: true,
        sortable: true,
        text: 'Vehicles',
    },
];
